// Walk-in prompt: "Trading Floor — Open Pumpr". Shown when the player stands in a zone.
import { hasKey, t, type TKey } from "../i18n";
import { dispatch, gameState, openApp, phoneOpen, showToast, world, zone } from "../bridge";
import type { ZoneAction } from "../../world/districts";
import { TUNABLES } from "@degen/shared/config/tunables";
import { money } from "@degen/shared/money";
import { dayOf } from "@degen/sim/state";
import { house, starterId } from "@degen/sim/houses/houses";
import { leaveRide } from "../../net/island";

/** The Home building shows which rented place you have (it depends on your status). */
function homeName() {
  const s = gameState();
  const id = s ? starterId(s) : null;
  return id ? `Home · ${house(id)!.name}` : "Home";
}

const LABEL: Record<ZoneAction, string> = {
  pumpr: "📈 Open Pumpr",
  wallet: "👛 Wallet",
  chirp: "🐦 Open Chirp",
  farm: "🌾 Open Farm",
  mintr: "🖼️ Open Mintr",
  launch_coin: "🚀 Launch a coin",
  launch_nft: "🎨 Launch NFTs",
  sleep: "😴 Sleep",
  laylow: "🫥 Lay low",
  settings: "⚙️ Settings",
  coffee: `☕ Coffee (${money(TUNABLES.player.coffee.costUsd)})`,
  grass: "🌱 Touch grass",
  enter: "🚪 Go inside",
  exit: "🚪 Leave",
  pets: "🐾 Your pets",
  adopt: "🐶 Adopt a pet",
  profile: "🏆 Profile",
  cars: "🚗 Browse cars",
  houses: "🏡 House details",
  messages: "💬 Messages",
  cook: `🍳 Cook (${money(TUNABLES.player.cook.costUsd)})`,
  drive: "🚗 Drive",
  park: "🅿️ Park & get out",
  vano: "🤖 Open Vano",
  hackathon: "🧑‍💻 Hackathons",
  gadgets: "📱 Shop phones",
  events: "🎤 What's on",
  testdrive: "🏁 Test drive (90s)",
  highway: "🛣️ Hit the highway",
  leave_highway: "🏝️ Exit to Degen Island",
  get_out: "🚪 Get out",
};

/** Localized labels where we have them (Pidgin), else the English default. */
function labelFor(a: ZoneAction): string {
  const key = `zone.${a}` as TKey;
  return hasKey(key) ? t(key) : LABEL[a];
}

export function runZoneAction(a: ZoneAction) {
  switch (a) {
    case "enter": {
      const z = zone.value;
      if (z) world()?.enter(z.id);
      return;
    }
    case "exit":
      return world()?.exit();
    case "pets":
      return openApp("pets");
    case "drive":
      return world()?.drive();
    case "park":
      return world()?.park();
    case "highway":
      return world()?.enterHighway();
    case "leave_highway":
      return world()?.leaveHighway();
    case "get_out":
      return leaveRide();
    case "messages":
      return openApp("messages");
    case "cook":
      if (dispatch({ type: "COOK" }).ok) showToast("🍳 Home cooking. You feel calmer.");
      return;
    case "cars": {
      // Zone ids: "polancco" outside, or "in:polancco:<modelId>" inside a showroom.
      const id = zone.value?.id ?? "";
      const parts = id.split(":");
      const dealer = parts[0] === "in" ? parts[1] : parts[0];
      const model = parts[0] === "in" && parts[2] !== "desk" ? parts[2] : undefined;
      return openApp("cars", model ? { dealer, model } : { dealer });
    }
    case "houses": {
      const id = zone.value?.id ?? "";
      const parts = id.split(":");
      return openApp("houses", { house: parts[0] === "in" ? parts[1] : parts[0] });
    }
    case "adopt":
      return openApp("pets", { tab: "adopt" });
    case "profile":
      return openApp("profile");
    case "pumpr":
      return openApp("pumpr");
    case "wallet":
      return openApp("wallet");
    case "chirp":
      return openApp("chirp");
    case "farm":
      return openApp("farm");
    case "mintr":
      return openApp("mintr");
    case "settings":
      return openApp("settings");
    case "launch_coin":
      return openApp("pumpr", { launch: "1" });
    case "launch_nft":
      return openApp("mintr", { launch: "1" });
    case "sleep": {
      const r = dispatch({ type: "SLEEP" });
      if (r.ok) showToast(`Slept like a baby. gm, it's day ${dayOf(gameState()!.tick)} ☀️`);
      return;
    }
    case "laylow":
      if (dispatch({ type: "LAY_LOW" }).ok) showToast("Laying low today. No posts, heat cools twice as fast.");
      return;
    case "coffee":
      if (dispatch({ type: "COFFEE" }).ok) showToast("☕ Deep breath. Heat cools a little.");
      return;
    case "vano":
      return openApp("vano");
    case "hackathon":
      return openApp("hackr");
    case "gadgets":
      return openApp("gadgets");
    case "events":
      return openApp("events");
    case "testdrive": {
      const parts = (zone.value?.id ?? "").split(":");
      if (parts[0] === "in" && parts[2]) world()?.testDrive(parts[2]);
      return;
    }
    case "grass":
      if (dispatch({ type: "TOUCH_GRASS" }).ok) showToast("🌱 You touched grass. Heat down, trust up. Wild.");
      return;
  }
}

export function ZonePrompt() {
  const z = zone.value;
  if (!z || phoneOpen.value) return null;
  return (
    <div class="zone-prompt" role="dialog" aria-label={z.name}>
      <div class="zone-name">{z.id === "home" ? homeName() : z.name}</div>
      <div class="zone-actions">
        {z.actions.map((a, i) => (
          <button key={a} class={`btn ${i === 0 ? "primary" : ""}`} onClick={() => runZoneAction(a)}>
            {labelFor(a)}
            {i === 0 && <kbd class="kbd">E</kbd>}
          </button>
        ))}
      </div>
    </div>
  );
}
